<script setup lang="ts">
import type { Profile } from '~/types/profile'
import { useProfileSelfStore } from '~/stores/user/Self'
import { useMagazineCategoriesStore } from '~/stores/magazine/Categories'
import { provide } from 'vue'
import type { LlanaListResponse } from '~/types/llana'
import type { MagazineCategory } from '~/types/magazine'
import IonicWrapper from './ionic-wrapper.vue'
import WebWrapper from './web-wrapper.vue'
import SearchModal from '~/components/Home/SearchModal.vue'
import { useProductCategoriesStore } from '~/stores/product/Categories'
import type { ProductCategory } from '~/types/products'
import { useForumStore } from '~/stores/forum/Forums'
import type { Forum } from '~/types/forum'
import type { NavigationItem } from '~/types/navigation'
import Toast, { type ToastNotification } from '~/components/Toast.vue'

const router = useRouter()
const path = ref<string>(router.currentRoute.value.path)

const { $llanaAuthCheck } = useNuxtApp()
const isAuthed = ref<boolean>(false)
const profile = ref<Profile | undefined>(undefined)
const toggleSearch = ref<boolean>(false)
const magazine_categories = ref<LlanaListResponse<MagazineCategory> | undefined>(undefined)
const product_categories = ref<LlanaListResponse<ProductCategory> | undefined>(undefined)
const forums = ref<LlanaListResponse<Forum> | undefined>(undefined)

// Provide refs first
provide('isAuthed', isAuthed)
provide('profile', profile)

try {
	isAuthed.value = $llanaAuthCheck()

	if (isAuthed.value) {
		const profileSelfStore = useProfileSelfStore()
		profile.value = await profileSelfStore().get()

		profileSelfStore().$subscribe((_mutation: any, state: Profile) => {
			profile.value = state
		})
	}
} catch (error) {
	console.error('Authentication check failed:', error)
	isAuthed.value = false
}

const desktop_navigation = [
	{ name: 'Home', href: '/', current: path.value === '/' },
	{ name: 'Challenges', href: '/challenges', current: path.value.startsWith('/challenges'), comingSoon: true },
	{ name: 'Discussions', href: '/discussions', current: path.value.startsWith('/discussions') },
	{ name: 'Products', href: '/products', current: path.value.startsWith('/products') },
	{ name: 'Magazine', href: '/magazine', current: path.value.startsWith('/magazine') },
]

const navigation: NavigationItem[] = [
	{ name: 'Home', href: '/', current: path.value === '/', icon: 'ph:house-duotone' },
	{
		name: 'Challenges',
		href: '/challenges',
		current: path.value.startsWith('/challenges'),
		icon: 'ph:trophy-duotone',
		comingSoon: true,
	},
	{
		name: 'Diet Plan',
		href: '/diet',
		current: path.value.startsWith('/diet'),
		icon: 'ph:avocado-duotone',
		comingSoon: true,
	},
	{
		name: 'Discussions',
		href: '/discussions',
		current: path.value.startsWith('/discussions'),
		icon: 'ph:chats-circle-duotone',
	},
	{
		name: 'Exercise',
		href: '/exercise',
		current: path.value.startsWith('/exercise'),
		icon: 'ph:barbell-duotone',
		comingSoon: true,
	},
	{
		name: 'Products',
		href: '/products',
		current: path.value.startsWith('/products'),
		icon: 'ph:shopping-bag-duotone',
	},
	{
		name: 'Magazine',
		href: '/magazine',
		current: path.value.startsWith('/magazine'),
		icon: 'ph:newspaper-clipping-duotone',
	},
	{
		name: 'Meetups',
		href: '/meetups',
		current: path.value.startsWith('/meetups'),
		icon: 'ph:users-duotone',
		comingSoon: true,
	},
	{
		name: 'About Us',
		href: '/about',
		current: path.value.startsWith('/about'),
		icon: 'ph:call-bell-duotone',
		hideMobile: true,
	},
	...(isAuthed.value && profile.value?.role === 'ADMIN'
		? [
				{
					name: 'Admin',
					href: '/admin',
					current: path.value.startsWith('/admin'),
					icon: 'ph:wrench-duotone',
				},
			]
		: []),
]

const submenu_logged_in = [
	{ name: 'Profile', href: '/profile', current: path.value.startsWith('/profile'), icon: 'ph:user-circle-duotone' },
	{ name: 'Logout', href: '/logout', current: path.value === '/logout', icon: 'ph:sign-out-duotone' },
]

const submenu_logged_out = [
	{ name: 'Login', href: '/login', current: path.value === '/login', icon: 'ph:sign-in-duotone' },
	{ name: 'Register', href: '/register', current: path.value === '/register', icon: 'ph:plus-circle-duotone' },
]

function resetMenu() {
	navigation.forEach(n => (n.current = false))
	desktop_navigation.forEach(n => (n.current = false))
	submenu_logged_in.forEach(n => (n.current = false))
	submenu_logged_out.forEach(n => (n.current = false))
}

function menuCurrent(): void {
	const value = router.currentRoute.value.path
	resetMenu()

	if (value === '/' || value === '') {
		navigation[0].current = true
		desktop_navigation[0].current = true
	}

	if (value.startsWith('/challenges')) {
		navigation[1].current = true
		desktop_navigation[1].current = true
	}

	if (value.startsWith('/diet')) {
		navigation[2].current = true
	}

	if (value.startsWith('/discussions')) {
		navigation[3].current = true
		desktop_navigation[2].current = true
	}

	if (value.startsWith('/exercise')) {
		navigation[4].current = true
	}

	if (value.startsWith('/products')) {
		navigation[5].current = true
		desktop_navigation[3].current = true
	}

	if (value.startsWith('/magazine')) {
		navigation[6].current = true
		desktop_navigation[4].current = true
	}

	if (value.startsWith('/meetups')) {
		navigation[7].current = true
	}

	if (value.startsWith('/about')) {
		navigation[8].current = true
	}

	if (value.startsWith('/admin')) {
		navigation[9].current = true
	}

	if (value.startsWith('/profile')) {
		submenu_logged_in[0].current = true
	}

	if (value === '/logout') {
		submenu_logged_in[1].current = true
	}

	if (value === '/login') {
		submenu_logged_out[0].current = true
	}

	if (value === '/register') {
		submenu_logged_out[1].current = true
	}
}

menuCurrent()

const magazineStore = useMagazineCategoriesStore()
magazine_categories.value = await magazineStore().list()
provide('magazine_categories', magazine_categories)

const productStore = useProductCategoriesStore()
product_categories.value = await productStore().list()
provide('product_categories', product_categories)

const forumStore = useForumStore()
forums.value = await forumStore().list()
provide('forums', forums)

const toast = ref<ToastNotification>({
	show: false,
})
provide('toast', toast)

watch(
	() => router.currentRoute.value.path,
	() => {
		path.value = router.currentRoute.value.path
		menuCurrent()
	},
)
</script>

<template>
	<component
		:is="useRenderingMode().isMobileApp ? IonicWrapper : WebWrapper"
		:navigation="navigation"
		:desktop_navigation="desktop_navigation"
		:is-authed="isAuthed"
		:profile="profile"
		:submenu_logged_in="submenu_logged_in"
		:submenu_logged_out="submenu_logged_out"
		@toggle-search="toggleSearch = !toggleSearch">
		<slot></slot>
	</component>

	<SearchModal v-if="toggleSearch" :show="toggleSearch" show-type-tag @close="toggleSearch = false" />

	<ClientOnly>
		<Toast v-if="toast.show" :toast="toast" />
	</ClientOnly>
</template>
